import type {
  Asset,
  AssetAttachment,
  AssetData,
  AssetFull,
  AssetSummary,
  AssetType
} from '@repo/common/types/Asset';
import type { EmailHeaders } from '@repo/common/types/Email';
import type { SerializedEditorState } from '@repo/common/types/Lexical';
import { uuidV4 } from '@repo/toolslib/src/utils/id.util';
import { derived, get, writable } from 'svelte/store';
import { getHandler, getHandlerAsync } from '../sharedlib';
import { waitForAuthAndUserId } from './waitForAuth';
type AssetStoreState = {
  summaries: Map<string, AssetSummary>;
  loading: boolean;
  error: string | null;
  lastLoadedAt?: Date;
};

const initialState: AssetStoreState = {
  summaries: new Map(),
  loading: false,
  error: null,
  lastLoadedAt: undefined
};

const assetStore = writable<AssetStoreState>(initialState);

let loadPromise: Promise<AssetSummary[] | void> | null = null;
let hasInitialized = false;

const toSummaryMap = (summaries: AssetSummary[]): Map<string, AssetSummary> => {
  const map = new Map<string, AssetSummary>();
  for (const summary of summaries) {
    // Normalize asset type in case future types are introduced
    const assetType = (summary.assetType as AssetType | undefined) ?? 'email';
    map.set(summary.id, {
      ...summary,
      assetType,
      createdAt: summary.createdAt ? new Date(summary.createdAt) : new Date(),
      updatedAt: summary.updatedAt ? new Date(summary.updatedAt) : new Date(),
      deletedAt: summary.deletedAt ? new Date(summary.deletedAt) : undefined
    });
  }
  return map;
};

export const assetState = {
  subscribe: assetStore.subscribe
};

export const assetSummaries = derived(assetStore, ($store) => $store.summaries);
export const assetSummaryList = derived(assetStore, ($store) =>
  Array.from($store.summaries.values())
);
export const assetsLoading = derived(assetStore, ($store) => $store.loading);
export const assetsError = derived(assetStore, ($store) => $store.error);
export const isLoading = derived(assetStore, ($store) => $store.loading);

export interface SaveAssetDraftInput {
  id?: string;
  assetType: AssetType;
  title: string;
  summary?: string | null;
  markdown?: string | null;
  contentHistory?: SerializedEditorState | null;
  workspaceId?: string;
  attachments?: AssetAttachment[] | null;
  emailHeaders?: EmailHeaders | undefined;
  data?: Record<string, unknown> | null;
  createdAt?: Date;
}

const buildAssetData = (input: SaveAssetDraftInput): AssetData | null => {
  const base: AssetData = {};
  if (input.emailHeaders) {
    base.emailHeaders = input.emailHeaders;
  }
  if (input.data) {
    Object.assign(base, input.data);
  }
  return Object.keys(base).length > 0 ? base : null;
};

const setErrorState = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? 'Unknown error');
  assetStore.update((state) => ({
    ...state,
    loading: false,
    error: message
  }));
};

const setSummariesState = (summaries: AssetSummary[]) => {
  assetStore.set({
    summaries: toSummaryMap(summaries),
    loading: false,
    error: null,
    lastLoadedAt: new Date()
  });
};

const loadSummariesInternal = async (force = false, limit = 1000): Promise<AssetSummary[]> => {
  const handler = getHandler();
  const summaries = await handler.loadAssetSummariesFromDB(force, limit);
  setSummariesState(summaries);
  hasInitialized = true;
  return summaries;
};

export async function ensureAssetSummariesLoaded(
  options: { force?: boolean; limit?: number } = {}
): Promise<void> {
  const { force = false, limit = 1000 } = options;
  const state = get(assetStore);

  if (state.loading && loadPromise) {
    await loadPromise;
    return;
  }

  if (!force && hasInitialized && state.summaries.size > 0) {
    return;
  }

  // Check if database initialization is in progress - if so, skip loading to avoid deadlock
  // The loading will happen automatically after initialization completes via asyncLoadDB
  const handler = await getHandlerAsync();
  if (!handler.isDatabaseLoaded()) {
    // Return resolved promise - loading will happen after initialization via asyncLoadDB
    return Promise.resolve();
  }

  assetStore.update((prev) => ({ ...prev, loading: true, error: null }));

  try {
    loadPromise = loadSummariesInternal(force, limit);
    await loadPromise;
  } catch (error) {
    setErrorState(error);
    throw error;
  } finally {
    loadPromise = null;
  }
}

export async function saveAssetDraft(input: SaveAssetDraftInput): Promise<AssetFull> {
  const handler = getHandler();
  const userId = handler.getUserId();
  if (!userId) {
    throw new Error('Cannot save asset without an authenticated user');
  }

  const models = await handler.getDatabaseModels();
  const assetModel = (models as any).assetModel as
    | { bulkUpsertItems: (items: Asset[]) => Promise<unknown> }
    | undefined;

  if (!assetModel || typeof assetModel.bulkUpsertItems !== 'function') {
    throw new Error('Asset model is not available');
  }

  const state = get(assetStore);
  const existingSummary = input.id ? state.summaries.get(input.id) : undefined;

  const assetId = input.id ?? existingSummary?.id ?? uuidV4();
  const createdAt = input.createdAt ?? existingSummary?.createdAt ?? new Date();
  const updatedAt = new Date();
  const summaryText = input.summary ?? input.title ?? undefined;
  const truncatedSummary = summaryText ? summaryText.slice(0, 500) : undefined;

  const { contentHistory } = input;

  if (process.env.NODE_ENV !== 'production') {
    console.log('[AssetStore] saveAssetDraft payload', {
      assetId,
      title: input.title,
      emailHeaders: input.emailHeaders,
      markdownLength: input.markdown?.length ?? 0,
      workspaceId: input.workspaceId,
      assetType: input.assetType
    });
  }

  const asset: AssetFull = {
    _type: 'assetFull',
    id: assetId,
    assetType: input.assetType,
    title: input.title || 'Untitled Asset',
    summary: truncatedSummary,
    content: input.markdown ?? null,
    contentHistory,
    data: buildAssetData(input),
    attachments: input.attachments ?? null,
    workspaceId: input.workspaceId ?? existingSummary?.workspaceId,
    userId,
    createdAt,
    updatedAt,
    deletedAt: undefined
  };

  if (process.env.NODE_ENV !== 'production') {
    console.log('[AssetStore] Upserting asset', {
      id: asset.id,
      content: asset.content,
      contentHistory: asset.contentHistory,
      contentHistoryStringified: JSON.stringify(asset.contentHistory),
      data: asset.data,
      title: asset.title
    });
  }

  await assetModel.bulkUpsertItems([asset]);

  if (process.env.NODE_ENV !== 'production') {
    console.log('[AssetStore] Asset upserted successfully', { id: asset.id });
  }

  // Update in-memory asset summaries so the Drive view shows the updated title
  const summary: AssetSummary = {
    _type: 'assetSummary',
    id: asset.id,
    assetType: asset.assetType,
    title: asset.title,
    summary: asset.summary,
    workspaceId: asset.workspaceId,
    userId: asset.userId,
    createdAt: asset.createdAt,
    updatedAt: asset.updatedAt,
    deletedAt: asset.deletedAt
  };
  upsertAssetSummaries([summary]);

  return asset;
}

export async function toggleAssetFavorite(id: string): Promise<boolean> {
  const handler = getHandler();
  const models = await handler.getDatabaseModels();
  const assetModel = models.assetModel;

  if (!assetModel || typeof assetModel.updateItem !== 'function') {
    throw new Error('Asset model is not available');
  }

  const state = get(assetStore);
  const summary = state.summaries.get(id);
  if (!summary) {
    console.warn(`[AssetStore] Asset ${id} not found for favorite toggle`);
    return false;
  }

  const newFavorite = !summary.isFavorite;
  const updatedSummary: AssetSummary = {
    ...summary,
    isFavorite: newFavorite,
    updatedAt: new Date()
  };

  // Update in database
  await assetModel.updateItem({
    id: summary.id,
    isFavorite: newFavorite,
    updatedAt: updatedSummary.updatedAt
  } as any);

  // Update in store
  upsertAssetSummaries([updatedSummary]);

  return true;
}

export async function getAssetById(id: string): Promise<AssetFull | null> {
  // Ensure userId is set before querying (required by toObj)
  await waitForAuthAndUserId();

  const handler = getHandler();
  const models = await handler.getDatabaseModels();
  const assetModel = models.assetModel;

  if (!assetModel || typeof assetModel.findAllObj !== 'function') {
    return null;
  }

  const assets = await assetModel.findAllObj({ where: { id }, limit: 1, paranoid: true });
  if (!Array.isArray(assets) || assets.length === 0) {
    return null;
  }

  return assets[0];
}

export function getAssetSummaryById(id: string): AssetSummary | null {
  // TODO: replace with a subscribed map
  const state = get(assetStore);
  return state.summaries.get(id) ?? null;
}

export function upsertAssetSummaries(summaries: AssetSummary[]): void {
  if (!summaries.length) return;
  assetStore.update((state) => {
    const updatedMap = new Map(state.summaries);
    for (const summary of summaries) {
      const normalized = {
        ...summary,
        assetType: (summary.assetType as AssetType | undefined) ?? 'email',
        createdAt: summary.createdAt ? new Date(summary.createdAt) : new Date(),
        updatedAt: summary.updatedAt ? new Date(summary.updatedAt) : new Date(),
        deletedAt: summary.deletedAt ? new Date(summary.deletedAt) : undefined
      };
      updatedMap.set(summary.id, normalized);
    }
    return {
      ...state,
      summaries: updatedMap
    };
  });
}

export async function softDeleteAsset(id: string): Promise<void> {
  const handler = getHandler();
  const models = await handler.getDatabaseModels();
  const assetModel = (models as any).assetModel as
    | { bulkUpsertItems: (items: Asset[]) => Promise<unknown> }
    | undefined;

  if (!assetModel || typeof assetModel.bulkUpsertItems !== 'function') {
    throw new Error('Asset model is not available');
  }

  const state = get(assetStore);
  const summary = state.summaries.get(id);
  if (!summary) {
    throw new Error('Asset not found');
  }

  const deletedAt = new Date();
  const updatedAsset: Asset = {
    _type: 'assetFull',
    id: summary.id,
    assetType: summary.assetType,
    title: summary.title,
    summary: summary.summary,
    content: null,
    contentHistory: null,
    data: null,
    attachments: null,
    workspaceId: summary.workspaceId,
    userId: summary.userId,
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
    deletedAt
  };

  await assetModel.bulkUpsertItems([updatedAsset]);

  // Update the summary in the store
  const updatedSummary: AssetSummary = {
    ...summary,
    deletedAt
  };
  upsertAssetSummaries([updatedSummary]);
}

export function removeAssetSummary(id: string): void {
  assetStore.update((state) => {
    if (!state.summaries.has(id)) {
      return state;
    }
    const updatedMap = new Map(state.summaries);
    updatedMap.delete(id);
    return {
      ...state,
      summaries: updatedMap
    };
  });
}

export function resetAssetStore(): void {
  assetStore.set(initialState);
  loadPromise = null;
  hasInitialized = false;
}
